(() => {
  if (customElements.get('sm-slide-tabs')) return;

  const desktop = window.matchMedia('(min-width: 990px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class SlideTabs extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.links = [...this.querySelectorAll('a[href]')];
      this.cursor = this.querySelector('.sm-slide-tabs__cursor');
      if (!this.cursor || !this.links.length) return;
      this.refined = this.closest('[data-sm-desktop-design]')?.dataset.smDesktopDesign === 'true';
      this.position = null;
      this.motionFrame = null;

      this.selected = this.links.find(link => link.getAttribute('aria-current') === 'page') || this.links[0];
      this.target = this.selected;

      for (const link of this.links) {
        link.addEventListener('pointerenter', () => this.moveTo(link), { signal });
        link.addEventListener('focus', () => this.moveTo(link), { signal });
      }
      this.addEventListener('pointerleave', () => this.moveTo(this.selected), { signal });
      this.addEventListener('focusout', event => {
        if (!this.contains(event.relatedTarget)) this.moveTo(this.selected);
      }, { signal });

      this.refresh = () => this.schedulePosition();
      desktop.addEventListener('change', this.refresh);
      reducedMotion.addEventListener('change', this.refresh);
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.refresh);
        this.resizeObserver.observe(this);
        this.links.forEach(link => this.resizeObserver.observe(link));
      } else {
        window.addEventListener('resize', this.refresh, { signal });
      }
      this.schedulePosition(true);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      desktop.removeEventListener('change', this.refresh);
      reducedMotion.removeEventListener('change', this.refresh);
      if (this.frame != null) cancelAnimationFrame(this.frame);
      if (this.motionFrame != null) cancelAnimationFrame(this.motionFrame);
      this.frame = null;
      this.motionFrame = null;
      this.position = null;
    }

    moveTo(link) {
      this.target = link;
      this.schedulePosition();
    }

    schedulePosition(initial = false) {
      if (this.frame != null) cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (!desktop.matches || !this.isConnected || !this.target) {
          this.stopMotion();
          return;
        }
        const bounds = this.getBoundingClientRect();
        const linkBounds = this.target.getBoundingClientRect();
        if (!linkBounds.width) return;
        if (this.refined) {
          this.targetPosition = { x: linkBounds.left - bounds.left, width: linkBounds.width };
          if (initial || !this.position || reducedMotion.matches) {
            this.stopMotion();
            this.position = { ...this.targetPosition, vx: 0, vw: 0 };
            this.paintPosition();
          } else if (this.motionFrame == null) {
            this.lastTime = performance.now();
            this.motionFrame = requestAnimationFrame(time => this.animatePosition(time));
          }
          this.classList.add('sm-slide-tabs--ready');
          return;
        }
        if (initial) this.cursor.style.transition = 'none';
        this.style.setProperty('--sm-slide-x', `${linkBounds.left - bounds.left}px`);
        this.style.setProperty('--sm-slide-width', `${linkBounds.width}px`);
        this.classList.add('sm-slide-tabs--ready');
        if (initial) requestAnimationFrame(() => this.cursor.style.removeProperty('transition'));
      });
    }

    stopMotion() {
      if (this.motionFrame != null) cancelAnimationFrame(this.motionFrame);
      this.motionFrame = null;
    }

    paintPosition() {
      this.style.setProperty('--sm-slide-x', `${this.position.x}px`);
      this.style.setProperty('--sm-slide-width', `${this.position.width}px`);
    }

    animatePosition(time) {
      this.motionFrame = null;
      if (!this.isConnected || !desktop.matches || !this.position) return;
      if (reducedMotion.matches) {
        this.position = { ...this.targetPosition, vx: 0, vw: 0 };
        this.paintPosition();
        return;
      }
      const dt = Math.min(.032, Math.max(0, (time - this.lastTime) / 1000));
      this.lastTime = time;
      // Critically damped analytic spring. Retargeting keeps current position and velocity.
      const omega = 26;
      const decay = Math.exp(-omega * dt);
      for (const [value, velocity] of [['x', 'vx'], ['width', 'vw']]) {
        const displacement = this.position[value] - this.targetPosition[value];
        const c = this.position[velocity] + omega * displacement;
        this.position[value] = this.targetPosition[value] + (displacement + c * dt) * decay;
        this.position[velocity] = (this.position[velocity] - omega * c * dt) * decay;
      }
      if (Math.abs(this.position.x - this.targetPosition.x) < .08 &&
          Math.abs(this.position.width - this.targetPosition.width) < .08 &&
          Math.abs(this.position.vx) < .5 && Math.abs(this.position.vw) < .5) {
        this.position = { ...this.targetPosition, vx: 0, vw: 0 };
        this.paintPosition();
        return;
      }
      this.paintPosition();
      this.motionFrame = requestAnimationFrame(next => this.animatePosition(next));
    }
  }

  customElements.define('sm-slide-tabs', SlideTabs);
})();
